import type { TradeAccountOption } from "../../../contexts/ActiveBrokerAccountContext";
import {
  formatBrokerConnectedAccountDisplayName,
  type BrokerConnectedAccountRow,
} from "./brokerConnectedAccountRow";
import {
  isTradeAccountAvailableForUi,
  isTradeAccountConnectedSyncedForUi,
} from "../../../utils/tradeAccountSource";

function rowFromTradeAccount(account: TradeAccountOption): BrokerConnectedAccountRow {
  return {
    accountCode: account.accountCode,
    displayName: formatBrokerConnectedAccountDisplayName(
      account.accountCode,
      account.customName,
      account.tradeLabel
    ),
    customName: account.customName ?? null,
    isDemo: account.isDemo,
  };
}

function sortRows(rows: BrokerConnectedAccountRow[]): BrokerConnectedAccountRow[] {
  return [...rows].sort((a, b) =>
    a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" })
  );
}

export type BrokerConnectedAccountRowsInput = {
  brokerSlug: string;
  tradeAccounts: TradeAccountOption[];
  logicalAccountCode?: string | null;
  logicalAccountDisplayName?: string | null;
  includeDiscoveredAccounts?: TradeAccountOption[];
  /** Account codes successfully synced this session — always Connected, even if BE source lags. */
  forceConnectedAccountCodes?: string[];
};

export function buildBrokerConnectedAccountRows(
  input: BrokerConnectedAccountRowsInput
): BrokerConnectedAccountRow[] {
  const rows = new Map<string, BrokerConnectedAccountRow>();

  const addRow = (row: BrokerConnectedAccountRow) => {
    const code = row.accountCode.trim();
    if (!code) return;
    rows.set(code, row);
  };

  const forceConnected = new Set(
    (input.forceConnectedAccountCodes ?? [])
      .map((code) => code.trim())
      .filter(Boolean)
  );

  for (const account of input.tradeAccounts) {
    const code = account.accountCode.trim();
    if (!forceConnected.has(code) && !isTradeAccountConnectedSyncedForUi(account)) continue;
    addRow(rowFromTradeAccount(account));
  }

  for (const account of input.includeDiscoveredAccounts ?? []) {
    const code = account.accountCode.trim();
    if (!forceConnected.has(code) || rows.has(code)) continue;
    addRow(rowFromTradeAccount(account));
  }

  Array.from(forceConnected).forEach((code) => {
    if (rows.has(code)) return;
    addRow({
      accountCode: code,
      displayName: formatBrokerConnectedAccountDisplayName(code, null, null),
      customName: null,
    });
  });

  if (rows.size === 0 && input.logicalAccountCode?.trim()) {
    addRow({
      accountCode: input.logicalAccountCode.trim(),
      displayName: input.logicalAccountDisplayName?.trim() || input.logicalAccountCode.trim(),
      customName: null,
    });
  }

  return sortRows(Array.from(rows.values()));
}

export function buildBrokerAvailableAccountRows(
  input: BrokerConnectedAccountRowsInput
): BrokerConnectedAccountRow[] {
  const rows = new Map<string, BrokerConnectedAccountRow>();
  const connectedCodes = new Set(
    buildBrokerConnectedAccountRows(input).map((row) => row.accountCode)
  );

  const addRow = (row: BrokerConnectedAccountRow) => {
    const code = row.accountCode.trim();
    if (!code || connectedCodes.has(code)) return;
    rows.set(code, row);
  };

  for (const account of input.tradeAccounts) {
    if (!isTradeAccountAvailableForUi(account)) continue;
    addRow(rowFromTradeAccount(account));
  }

  for (const account of input.includeDiscoveredAccounts ?? []) {
    if (!isTradeAccountAvailableForUi(account)) continue;
    addRow(rowFromTradeAccount(account));
  }

  return sortRows(Array.from(rows.values()));
}
